<template>
  <main>
    <b-loading :is-full-page="true" v-model="loading"></b-loading>
    <alert-modal
      v-if="isSaveNetworkModalOpen"
      title="Are you sure you want to exclude the pre-enrollment site?"
      text="There will be no PE site for this client, as none of the networks are selected to be included."
      primaryBtnText="Save"
      secondaryBtnText="Cancel"
      @primaryBtnClick="createOrUpdateClient"
      @secondaryBtnClick="isSaveNetworkModalOpen = false"
      @close="isSaveNetworkModalOpen = false"
    />
    <alert-modal
      v-if="isSaveModalOpen"
      title="Save your work without publishing the pre-enrollment site"
      text="The configurations is completed but you have opted not to publish. You can come back at a later date to publish."
      primaryBtnText="Not yet, just save my work"
      secondaryBtnText="Publish pre-enrollment site"
      dismissible
      @primaryBtnClick="createOrUpdateClient"
      @secondaryBtnClick="submitPreEnrollment"
      @close="isSaveModalOpen = false"
    />
    <alert-modal
      v-if="isSavePlanAffiliationModalOpen"
      title="One or more plans are not associated with a network"
      text="Provider and network data will not be available for these plans"
      primaryBtnText="Save anyway"
      secondaryBtnText="Keep working"
      dismissible
      @primaryBtnClick="onAffiliationModalConfirm"
      @secondaryBtnClick="onKeepWorking"
      @close="isSavePlanAffiliationModalOpen = false"
    />
    <alert-modal
      v-if="getShowNetworkAlertModal"
      title="Unsaved work!"
      text="Your work has not been saved and will be lost."
      secondaryBtnText="Continue working"
      primaryBtnText="Leave this page"
      @primaryBtnClick="goHome"
      @secondaryBtnClick="keepChanges"
    />
    <div class="config--save-container">
      <alert
        v-if="saveError && (preEnrollment.invalid || networks.invalid)"
        type="error"
        class="save--alert"
      >
        <span v-if="networks.invalid && preEnrollment.invalid"
          >Please fix all errors, and try saving again.</span
        >
        <span v-if="networks.invalid && !preEnrollment.invalid">
          Please fix the errors in the
          <span class="error--bold"> Networks </span>
          tab, and try saving again.
        </span>
        <span v-if="preEnrollment.invalid && !networks.invalid">
          Please fix the errors in the
          <span class="error--bold"> Pre-enrollment </span>
          tab, and try saving again.
        </span>
      </alert>
      <alert v-if="unexpectedSaveError" type="error" class="save--alert">
        An unexpected error has occurred while saving.
      </alert>
      <alert v-if="savedSuccess" type="success" class="save--alert">
        You have successfully submitted your work.
      </alert>

      <b-button class="save" @click="onClickSave" :disabled="!saveEnabled"
        >Save</b-button
      >
    </div>

    <b-tabs class="network--tabs" v-model="activeTab">
      <b-tab-item label="Networks">
        <client-form
          :networks="networksFormState"
          @client-name-input="onClientNameInput"
          @network-add="onNetworkAdd"
          @network-delete="onNetworkDelete"
          @network-save-check="onNetworksSaveCheck"
          @hide-validation-errors="hideValidationErrors"
          @wipe-plan-affiliation="handleWipePlanAffiliation"
        />
      </b-tab-item>

      <b-tab-item label="Pre-enrollment" :disabled="!networks.preEnrollmentTabEnabled">
        <b-tooltip
          slot="header"
          label="This tab is not available based on your network configurations. This tab will be available if at least one network is configured to be included on the OE site."
          :animated="false"
          :triggers="!networks.preEnrollmentTabEnabled ? undefined : []"
        >
          <span>Pre-enrollment</span>
        </b-tooltip>
        <pre-enrollment-form
          :pre-enrollment="preEnrollmentFormState"
          @custom-content-input="onPreEnrollmentCustomContentInput"
          @confirm-custom-content-remove="onPreEnrollmentConfirmCustomContentRemove"
          @site-url-input="onPreEnrollmentSiteUrlInput"
          @url-blur="onPreEnrollmentUrlCheck"
          @url-keydown="onPreEnrollmentUrlCheck"
          @edit-url-click="onPreEnrollmentEditUrlClick"
          @confirm-edit-url="onPreEnrollmentConfirmEditUrl"
          @close-confirm-edit-url-modal="preEnrollment.isConfirmEditUrlModalOpen = false"
          @publish-click="onPreEnrollmentPublishClick"
          @publish-modal-submit="onPreEnrollmentModalSubmit"
          @unpublish-modal-submit="onPreEnrollmentModalSubmit"
          @close-publish-modal="preEnrollment.isPublishModalOpen = false"
          @close-unpublish-modal="preEnrollment.isUnpublishModalOpen = false"
          @close-confirm-remove-custom-modal="preEnrollment.isConfirmRemoveCustomModalOpen = false"
        />
      </b-tab-item>
      <b-tab-item
        label="Plan affiliation"
        :disabled="!planAffiliation.enabledTab"
      >
        <PlanAffiliationsForm
          v-if="networks.networkNames"
          :key="planAffiliation.reloadKey"
          :planAffiliation="planAffiliationFormState"
          @plan-type-change="onPlanAffiliationPlanTypeChange"
          @client-id-change="onPlanAffiliationClientIdChange"
          @network-affiliation-change="onPlanAffiliationNetworkAffiliationChange"
        />
      </b-tab-item>
      <b-tab-item
        headerClass="tabs--client-name"
        v-if="this.$route.params.clientName"
        :disabled="true"
      >
        <template slot="header">{{ this.$route.params.clientName }}</template>
      </b-tab-item>
    </b-tabs>

  </main>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import ClientForm from '@/components/client-form.vue'
import PreEnrollmentForm from '@/components/pre-enrollment-form/pre-enrollment-form.vue'
import PlanAffiliationsForm from '@/components/plan-affiliations-form/plan-affiliations-form.vue'

import AlertModal from '@/components/alert-modal.vue'
import Alert from '@/components/alert.vue'
import client from '@/api/networkConfigApi'
import { isSuccessResponse } from '@/helpers/is-success-response'
import { cloneDeep, get } from 'lodash'
import store from '@/store/store'
import { createNewConfigArray, addClientConfig } from '@/helpers/add-client-config'
import planApi from '@/api/planApi'
import {
  parseNetworkNameOptions,
  parseNetworkAffiliationUuids,
  solveAffiliationRequestBody,
  parsePlansPartitionedByNamespace,
  solveNetworkAffiliationUpdates,
  mapClientIdFromPlanTypeSelection,
  solveUserNetworkAffiliationChange,
  resolveEnableSavePlanAffiliationPayload,
  resolveClientIdFromSelectPayload,
  mapNetworkAffiliationFromSelectPayload,
  solvePlanAffiliationState,
  resolveNetworksClientNameError,
  resolveNetworksFormSaveEnabled,
  parseNetworkConfigsWithAppendedRow,
  parseNetworkConfigsAfterRowDelete,
  parseEvenOptionIndicesFromSeriesCount,
  formatDomainSuffixFromEnv,
  resolvePreEnrollmentUrlFormatError,
  resolvePreEnrollmentInvalidFromUrlCheck,
  resolveIsPublishDisabled,
  resolveConfirmCustomContentRemoval,
  validateSkipUrlCheckOnEvent,
  resolvePreEnrollmentTabEnabled,
  resolvePlanAffiliationTabEnabled,
  resolveNetworksSaveEnabled,
  resolveSaveModalAction,
  resolveConfirmPlanAffiliationSave,
  resolvePreEnrollmentSubmitNeedsSave,
  parseExternalRbpConfigsOnLoad,
  mapMissingPlanAffiliationsPayload,
  resolvePreEnrollmentUiState,
  parseComparableClientConfig,
  parseComparableFromClientData,
  parseEmptyClientData,
  parseClientDataFromResponse,
  resolveHasUnsavedChanges,
  solveClientConfigSavePayload
} from '@/utils/clientConfigUtils'

export default {
  name: 'client-config',
  components: {
    AlertModal,
    ClientForm,
    PreEnrollmentForm,
    Alert,
    PlanAffiliationsForm
  },
  data () {
    return {
      activeTab: 0,
      isSaveNetworkModalOpen: false,
      isSaveModalOpen: false,
      isSavePlanAffiliationModalOpen: false,
      saveError: false,
      savedSuccess: false,
      unexpectedSaveError: false,
      saveEnabled: false,
      loading: false,
      clientData: parseEmptyClientData(),
      networks: {
        name: '',
        networkConfigs: createNewConfigArray(),
        clientNameError: undefined,
        seriesOptions: {},
        evenOptions: [],
        loaded: false,
        lastIndex: 0,
        initialName: '',
        showDeepValidationErrors: false,
        invalid: false,
        preEnrollmentTabEnabled: false,
        networkNames: []
      },
      preEnrollment: {
        siteURL: '',
        customContent: false,
        publish: false,
        hasCustomization: false,
        invalid: false,
        urlError: null,
        isUrlUnique: null,
        isUrlValid: false,
        isCheckingUrl: false,
        shouldShowUrlCheck: false,
        isOeUrlDisabled: false,
        showUnpublishWarning: false,
        canDeleteCustomContent: false,
        isConfirmEditUrlModalOpen: false,
        isPublishModalOpen: false,
        isUnpublishModalOpen: false,
        isPublishModalSuccess: false,
        isUnpublishModalSuccess: false,
        isConfirmRemoveCustomModalOpen: false
      },
      planAffiliation: {
        planType: '',
        clientId: '',
        configuredClient: null,
        selectedClient: { title: 'NONE', value: '' },
        clientOptions: [],
        jvPlansGroupedByClientId: {},
        hrpPlansGroupedByClientId: {},
        reloadKey: 0,
        enabledTab: false,
        clientPlansList: [],
        planAffiliations: undefined
      }
    }
  },
  computed: {
    ...mapGetters('networkConfig', [
      'getShowNetworkAlertModal',
      'getNetworkHasChanged'
    ]),
    planAffiliationNetworkUuids () {
      return parseNetworkAffiliationUuids(this.planAffiliation.planAffiliations?.plans ?? [])
    },
    clientNameList () {
      return store.getters['networkConfig/getClientNameList']
    },
    preEnrollmentDomainSuffix () {
      return formatDomainSuffixFromEnv()
    },
    preEnrollmentIsPublishDisabled () {
      return resolveIsPublishDisabled({
        siteURL: this.preEnrollment.siteURL,
        customContent: this.preEnrollment.customContent,
        isUrlUnique: this.preEnrollment.isUrlUnique,
        isUrlValid: this.preEnrollment.isUrlValid,
        urlError: this.preEnrollment.urlError
      })
    },
    preEnrollmentPersistedCustomContent () {
      return this.clientData?.preEnrollment?.customContent
    },
    networksFormState () {
      return {
        ...this.networks,
        planAffiliationNetworkUuids: this.planAffiliationNetworkUuids
      }
    },
    preEnrollmentFormState () {
      return {
        ...this.preEnrollment,
        domainName: this.preEnrollmentDomainSuffix,
        isPublishDisabled: this.preEnrollmentIsPublishDisabled
      }
    },
    planAffiliationFormState () {
      const { planType, clientId, configuredClient, selectedClient, clientOptions } = this.planAffiliation
      return { planType, clientId, configuredClient, selectedClient, clientOptions }
    }
  },
  methods: {
    ...mapActions([
      'networkConfig/setShowNetworkAlertModal',
      'networkConfig/setNetworkHasChanged'
    ]),
    setLoading (value) {
      this.loading = value
    },
    buildTabDomainSnapshot () {
      return {
        name: this.networks.name,
        networkConfigs: this.networks.networkConfigs,
        preEnrollment: {
          siteURL: this.preEnrollment.siteURL,
          customContent: this.preEnrollment.customContent,
          publish: this.preEnrollment.publish,
          hasCustomization: this.preEnrollment.hasCustomization
        },
        planAffiliations: this.planAffiliation.planAffiliations
      }
    },
    resolveTabUnsavedChanges () {
      return resolveHasUnsavedChanges(
        parseComparableClientConfig(this.buildTabDomainSnapshot()),
        parseComparableFromClientData(this.clientData)
      )
    },
    syncUnsavedChangesFlag () {
      this['networkConfig/setNetworkHasChanged'](this.resolveTabUnsavedChanges())
    },
    saveIntoTabStatesFromClientData (clientData) {
      if (!clientData) {
        return
      }

      this.networks.name = clientData.name ?? ''
      this.networks.networkConfigs =
        clientData.networkConfigs?.length > 0
          ? parseExternalRbpConfigsOnLoad(clientData.networkConfigs)
          : createNewConfigArray()
      this.networks.initialName = clientData.name ? clientData.name.toLowerCase() : ''
      this.networks.lastIndex = this.networks.networkConfigs.length - 1
      this.networks.loaded = true

      if (clientData.preEnrollment) {
        this.preEnrollment.siteURL = clientData.preEnrollment.siteURL ?? ''
        this.preEnrollment.customContent = clientData.preEnrollment.customContent ?? false
        this.preEnrollment.publish = clientData.preEnrollment.publish ?? false
        this.preEnrollment.hasCustomization = clientData.preEnrollment.hasCustomization ?? false
      } else {
        this.preEnrollment.siteURL = ''
        this.preEnrollment.customContent = false
        this.preEnrollment.publish = false
        this.preEnrollment.hasCustomization = false
      }

      this.planAffiliation.planAffiliations = clientData.planAffiliations
        ? cloneDeep(clientData.planAffiliations)
        : undefined

      this.syncPreEnrollmentUi({ resetUrlCheck: true })
    },
    enableSavePlanAffiliation (planAffiliations) {
      this.saveEnabled = !!planAffiliations
      if (this.saveEnabled) {
        this.planAffiliation.planAffiliations = planAffiliations
      }
      this.syncUnsavedChangesFlag()
    },
    handleWipePlanAffiliation () {
      this.planAffiliation.planAffiliations = { title: 'NONE' }
      this.$nextTick(() => {
        this.syncPlanAffiliationState({ mode: 'reset', remountForm: true })
      })
      this.$buefy.toast.open({
        message: 'Plan affiliations have been wiped!',
        type: 'is-info',
        duration: 5000,
        position: 'is-top'
      })
      this.syncUnsavedChangesFlag()
    },
    setSaveEnabled (formNoErrors) {
      this.networks.preEnrollmentTabEnabled = resolvePreEnrollmentTabEnabled(this.networks.networkConfigs)
      this.planAffiliation.enabledTab = resolvePlanAffiliationTabEnabled(this.clientData)
      this.saveEnabled = resolveNetworksSaveEnabled({
        name: this.networks.name,
        networkConfigs: this.networks.networkConfigs,
        formNoErrors,
        hasUnsavedChanges: this.resolveTabUnsavedChanges()
      })
      this.syncUnsavedChangesFlag()
    },
    async onClickSave () {
      const isPublished = get(this.clientData, 'preEnrollment.publish')
      const saveAction = resolveSaveModalAction({
        activeTab: this.activeTab,
        isPublished,
        networkConfigs: this.networks.networkConfigs,
        customContent: this.preEnrollment.customContent
      })

      if (saveAction === 'open-save-network-modal') {
        this.savedSuccess = false
        this.saveError = false
        this.unexpectedSaveError = false
        this.isSaveNetworkModalOpen = true
        return
      }

      if (saveAction === 'noop') {
        return
      }

      if (saveAction === 'open-save-pre-enrollment-modal') {
        this.isSaveModalOpen = true
        return
      }

      this.createOrUpdateClient()
    },
    onAffiliationModalConfirm () {
      this.onSavePlanAffiliations(false)
      this.createOrUpdateClient()
      this.isSavePlanAffiliationModalOpen = false
    },
    async createOrUpdateClient (showAlert = true) {
      this.isSaveNetworkModalOpen = false
      this.isSaveModalOpen = false
      this.savedSuccess = false
      this.saveError = false
      this.unexpectedSaveError = false
      this.networks.showDeepValidationErrors = false
      this.networks.invalid = false

      const saveResult = solveClientConfigSavePayload(this.buildTabDomainSnapshot())

      if (!saveResult.valid) {
        this.networks.invalid = saveResult.networksInvalid
        this.networks.showDeepValidationErrors = true
        this.saveError = true
        return
      }

      if (this.preEnrollment.invalid) {
        this.saveError = true
        return
      }

      const data = saveResult.payload
      let response

      try {
        if (this.$route.params.clientName) {
          response = await client.updateClientConfig(
            this.clientData?.name,
            data
          )
        } else {
          response = await client.createClientConfig(data)
        }

        if (isSuccessResponse(response)) {
          await this.onSavePlanAffiliations()
          this['networkConfig/setNetworkHasChanged'](false)
          this.clientData = parseClientDataFromResponse({
            ...response.data,
            planAffiliations: cloneDeep(this.planAffiliation.planAffiliations)
          })
          this.saveIntoTabStatesFromClientData(this.clientData)

          if (this.$route.params.clientName !== data.name) {
            this.$router.replace({
              name: 'ClientConfig',
              params: { clientName: data.name },
              silent: true
            })
          }

          if (showAlert) {
            this.savedSuccess = true
          }
        } else {
          console.error(response.data)
          this.unexpectedSaveError = true
        }
      } catch (e) {
        console.error(e)
        this.unexpectedSaveError = true
      } finally {
        this.setLoading(false)
      }

      this.isSaveModalOpen = false

      return response
    },
    getNetworkNames () {
      const networkNames = parseNetworkNameOptions(this.networks.networkConfigs)

      this.networks.networkNames = networkNames

      return networkNames
    },
    hideValidationErrors () {
      this.networks.showDeepValidationErrors = false
    },
    async onSavePlanAffiliations (confirmAction = true) {
      if (confirmAction && resolveConfirmPlanAffiliationSave(this.planAffiliation.clientPlansList)) {
        this.isSavePlanAffiliationModalOpen = true

        return false
      }
      this.setLoading(true)
      const changeAffiliationRequestBody = solveAffiliationRequestBody(
        this.planAffiliation.planAffiliations,
        this.networks.name
      )

      try {
        const res = await planApi.updatePlanNetworkAffiliationForClient(
          changeAffiliationRequestBody
        )
        const data = res.data
        // update store without refetching all plans
        const plansToUpdateNetworkAffilication = [...data.added, ...data.removed]
        if (plansToUpdateNetworkAffilication.length) {
          const { jvPlans: jvPlansToUpdateNetworkAffiliation, hrpPlans: hrpPlansToUpdateNetworkAffiliation } =
            parsePlansPartitionedByNamespace(plansToUpdateNetworkAffilication)

          if (jvPlansToUpdateNetworkAffiliation.length) {
            const jvPlans = store.state.plans.jvPlans
            await store.dispatch('plans/updateJvPlans', solveNetworkAffiliationUpdates(jvPlans, jvPlansToUpdateNetworkAffiliation))
          }

          if (hrpPlansToUpdateNetworkAffiliation.length) {
            const hrpPlans = store.state.plans.hrpPlans
            await store.dispatch('plans/updateHrpPlans', solveNetworkAffiliationUpdates(hrpPlans, hrpPlansToUpdateNetworkAffiliation))
          }
        }

        this.getNetworkNames()

        this['networkConfig/setNetworkHasChanged'](false)
        this.savedSuccess = true
        this.syncPlanAffiliationState({ mode: 'fromStore', remountForm: true })
        this.syncPlanAffiliationSaveState()
      } catch (error) {
        console.log(error)
        this.saveError = true
      } finally {
        this.isSavePlanAffiliationModalOpen = false
      }
    },
    onKeepWorking () {
      this['networkConfig/setShowNetworkAlertModal'](false)
      this.isSavePlanAffiliationModalOpen = false
    },
    async submitPreEnrollment (data) {
      if (resolvePreEnrollmentSubmitNeedsSave(this.buildTabDomainSnapshot(), this.clientData)) {
        const response = await this.createOrUpdateClient(false)

        if (!response || !isSuccessResponse(response)) {
          return
        }
      } else {
        this.isSaveModalOpen = false
      }

      const isPublished = get(this.clientData, 'preEnrollment.publish')

      let response
      if (isPublished) {
        response = await client.unpublishOESite(this.clientData.name)
      } else {
        response = await client.publishOESite(this.clientData.name)
      }

      if (isSuccessResponse(response)) {
        this.clientData = parseClientDataFromResponse(response.data)
        this.saveIntoTabStatesFromClientData(this.clientData)
      }

      return response
    },
    goHome () {
      const toPath = this.getShowNetworkAlertModal || '/plans'

      this['networkConfig/setNetworkHasChanged'](false)
      this.$router.push({ path: toPath }).catch(() => {})
    },
    keepChanges () {
      this['networkConfig/setShowNetworkAlertModal'](false)
    },
    async loadNetworkSeriesOptions () {
      try {
        await store.dispatch('networkConfig/getNetworkSeriesOptions')
        this.networks.seriesOptions = store.getters['networkConfig/getNetworkSeriesOptions']
        this.networks.evenOptions = parseEvenOptionIndicesFromSeriesCount(
          Object.keys(this.networks.seriesOptions).length
        )
      } catch (error) {
        console.error(error)
      }
    },
    onClientNameInput (name) {
      this.networks.name = name
      this.networks.clientNameError = resolveNetworksClientNameError({
        name: this.networks.name,
        initialName: this.networks.initialName,
        clientNameList: this.clientNameList
      })
      this.onNetworksSaveCheck()
    },
    onNetworkAdd () {
      this.hideValidationErrors()
      const newRow = addClientConfig()
      this.networks.networkConfigs = parseNetworkConfigsWithAppendedRow(this.networks.networkConfigs, newRow)
      this.networks.lastIndex = this.networks.networkConfigs.length - 1
      this.onNetworksSaveCheck()
    },
    onNetworkDelete (index) {
      const { networkConfigs, lastIndex } = parseNetworkConfigsAfterRowDelete({
        networkConfigs: this.networks.networkConfigs,
        index,
        emptyRow: addClientConfig()
      })
      this.networks.networkConfigs = networkConfigs
      this.networks.lastIndex = lastIndex
      this.onNetworksSaveCheck()
    },
    onNetworksSaveCheck () {
      const formNoErrors = resolveNetworksFormSaveEnabled({
        name: this.networks.name,
        clientNameError: this.networks.clientNameError,
        networkConfigs: this.networks.networkConfigs
      })
      this.setSaveEnabled(formNoErrors)
    },
    setPreEnrollmentInvalid (invalid) {
      this.preEnrollment.invalid = invalid
    },
    syncPreEnrollmentUi ({ resetUrlCheck = false } = {}) {
      const uiState = resolvePreEnrollmentUiState(
        {
          siteURL: this.preEnrollment.siteURL,
          publish: this.preEnrollment.publish
        },
        { resetUrlCheck }
      )
      Object.assign(this.preEnrollment, uiState)
    },
    onPreEnrollmentCustomContentInput (value) {
      if (
        value === false &&
        resolveConfirmCustomContentRemoval({
          canDeleteCustomContent: this.preEnrollment.canDeleteCustomContent,
          persistedCustomContent: this.preEnrollmentPersistedCustomContent
        })
      ) {
        this.$nextTick(() => {
          this.preEnrollment.customContent = true
        })
        this.preEnrollment.isConfirmRemoveCustomModalOpen = true
        return
      }
      this.preEnrollment.customContent = value
      this.syncUnsavedChangesFlag()
    },
    onPreEnrollmentConfirmCustomContentRemove () {
      this.preEnrollment.customContent = false
      this.preEnrollment.isConfirmRemoveCustomModalOpen = false
      this.preEnrollment.canDeleteCustomContent = true
      this.syncUnsavedChangesFlag()
    },
    onPreEnrollmentSiteUrlInput (url) {
      this.preEnrollment.siteURL = url
      this.syncUnsavedChangesFlag()
    },
    async onPreEnrollmentUrlCheck (event) {
      if (
        validateSkipUrlCheckOnEvent({
          eventType: event.type,
          key: event.key,
          isUrlUnique: this.preEnrollment.isUrlUnique
        })
      ) {
        return
      }

      this.preEnrollment.urlError = null
      this.preEnrollment.isUrlUnique = null
      this.preEnrollment.shouldShowUrlCheck = false
      const url = event.target.value

      const urlFormatError = resolvePreEnrollmentUrlFormatError(url)
      if (urlFormatError) {
        this.preEnrollment.urlError = urlFormatError
        this.setPreEnrollmentInvalid(
          resolvePreEnrollmentInvalidFromUrlCheck({ urlFormatError })
        )
        return
      }

      this.preEnrollment.isCheckingUrl = true
      this.preEnrollment.shouldShowUrlCheck = true
      try {
        const { unique, valid } = await client.checkOeURL(url)

        this.preEnrollment.isUrlUnique = unique
        this.preEnrollment.isUrlValid = valid
        this.setPreEnrollmentInvalid(
          resolvePreEnrollmentInvalidFromUrlCheck({ unique, valid })
        )

        if (!valid) {
          this.preEnrollment.urlError = 'URL is invalid'
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.preEnrollment.isCheckingUrl = false
      }
    },
    onPreEnrollmentEditUrlClick () {
      this.preEnrollment.isConfirmEditUrlModalOpen = true
    },
    onPreEnrollmentConfirmEditUrl () {
      this.preEnrollment.isOeUrlDisabled = false
      this.preEnrollment.isConfirmEditUrlModalOpen = false
    },
    onPreEnrollmentPublishClick () {
      if (this.clientData?.preEnrollment?.customContent) {
        return
      }

      this.preEnrollment.isPublishModalSuccess = false
      this.preEnrollment.isUnpublishModalSuccess = false

      if (this.preEnrollment.publish) {
        this.preEnrollment.isUnpublishModalOpen = true
        return
      }

      this.preEnrollment.isPublishModalOpen = true
    },
    async onPreEnrollmentModalSubmit () {
      this.preEnrollment.isPublishModalSuccess = false
      this.preEnrollment.isUnpublishModalSuccess = false

      const response = await this.submitPreEnrollment()

      if (isSuccessResponse(response)) {
        this.preEnrollment.isPublishModalSuccess = true
        this.preEnrollment.isUnpublishModalSuccess = true
      } else {
        this.preEnrollment.isPublishModalOpen = false
        this.preEnrollment.isUnpublishModalOpen = false
      }
    },
    syncPlanAffiliationState ({ mode = 'fromStore', remountForm = false, selection = null } = {}) {
      this.getNetworkNames()

      const built = solvePlanAffiliationState({
        jvPlans: store.state.plans.jvPlans,
        hrpPlans: store.state.plans.hrpPlans,
        networkNames: this.networks.networkNames,
        currentClientName: this.$route.params.clientName,
        selection: mode === 'preserveSelection'
          ? (selection ?? {
            planType: this.planAffiliation.planType,
            clientId: this.planAffiliation.clientId
          })
          : null,
        reset: mode === 'reset'
      })

      this.planAffiliation.jvPlansGroupedByClientId = built.jvPlansGroupedByClientId
      this.planAffiliation.hrpPlansGroupedByClientId = built.hrpPlansGroupedByClientId
      this.planAffiliation.planType = built.planType
      this.planAffiliation.clientId = built.clientId
      this.planAffiliation.configuredClient = built.configuredClient
      this.planAffiliation.selectedClient = built.selectedClient
      this.planAffiliation.clientOptions = built.clientOptions

      if (mode === 'fromStore' && built.selectedClient?.plans) {
        this.planAffiliation.clientPlansList = built.selectedClient.plans
      }

      if (remountForm) {
        this.planAffiliation.reloadKey++
      }
    },
    onPlanAffiliationPlanTypeChange (planType) {
      const { clientId } = mapClientIdFromPlanTypeSelection(
        planType,
        this.planAffiliation.configuredClient
      )
      this.syncPlanAffiliationState({
        mode: 'preserveSelection',
        selection: { planType, clientId }
      })
      this.syncPlanAffiliationSaveState()
    },
    onPlanAffiliationClientIdChange (e) {
      const clientId = resolveClientIdFromSelectPayload(e.target)
      this.syncPlanAffiliationState({
        mode: 'preserveSelection',
        selection: {
          planType: this.planAffiliation.planType,
          clientId
        }
      })
      this.syncPlanAffiliationSaveState()
    },
    onPlanAffiliationNetworkAffiliationChange (e) {
      const { planId, networkUuid } = mapNetworkAffiliationFromSelectPayload(e.target)
      this.planAffiliation.selectedClient = solveUserNetworkAffiliationChange({
        selectedClient: this.planAffiliation.selectedClient,
        planId,
        networkUuid,
        currentClientName: this.$route.params.clientName
      })
      this.syncPlanAffiliationSaveState()
    },
    syncPlanAffiliationSaveState () {
      this.enableSavePlanAffiliation(
        resolveEnableSavePlanAffiliationPayload(
          this.planAffiliation.selectedClient,
          this.planAffiliation.planType
        )
      )
    }
  },
  async created () {
    if (!store.state.plans.jvPlans?.length || !store.state.plans.hrpPlans?.length) {
      await store.dispatch('plans/getAllPlans')
    }

    this.setLoading(true)

    await this.loadNetworkSeriesOptions()

    let clientLoadedWithoutPlanAffiliations = false

    if (this.$route.params.clientName) {
      await store.dispatch('networkConfig/getClientConfigs')

      this.isEdit = true
      const response = await client.getClient(this.$route.params.clientName)

      if (isSuccessResponse(response)) {
        const parsedClientData = parseClientDataFromResponse(response.data)

        if (!response.data.planAffiliations) {
          clientLoadedWithoutPlanAffiliations = true
        }

        if (!parsedClientData.networkConfigs || parsedClientData.networkConfigs.length < 1) {
          parsedClientData.networkConfigs = createNewConfigArray()
        } else {
          parsedClientData.networkConfigs = parseExternalRbpConfigsOnLoad(parsedClientData.networkConfigs)
        }
        this.clientData = parsedClientData
        this.saveIntoTabStatesFromClientData(this.clientData)
      }
    }

    this.getNetworkNames()
    this.syncPlanAffiliationState({ mode: 'fromStore', remountForm: true })

    if (clientLoadedWithoutPlanAffiliations) {
      if (this.planAffiliation.configuredClient) {
        this.planAffiliation.planAffiliations = resolveEnableSavePlanAffiliationPayload(
          this.planAffiliation.selectedClient,
          this.planAffiliation.planType
        )
        this.planAffiliation.clientPlansList = this.planAffiliation.selectedClient?.plans ?? []
      } else {
        const allStorePlans = [
          ...store.state.plans.jvPlans,
          ...store.state.plans.hrpPlans
        ]
        this.planAffiliation.planAffiliations = mapMissingPlanAffiliationsPayload(allStorePlans)
      }
      this.syncUnsavedChangesFlag()
    }

    this.setLoading(false)
  },
  watch: {
    clientData: {
      handler: function (newVal) {
        this.saveIntoTabStatesFromClientData(newVal)
        this.getNetworkNames()

        this.syncPlanAffiliationState({ mode: 'fromStore' })
        this['networkConfig/setNetworkHasChanged'](false)
      },
      deep: true
    },
    'networks.networkConfigs': {
      handler () {
        this.getNetworkNames()
        this.syncPlanAffiliationState({ mode: 'preserveSelection' })
        this.syncUnsavedChangesFlag()
      },
      deep: true
    },
    activeTab: function () {
      this.saveError = false
      this.unexpectedSaveError = false
      this.savedSuccess = false
    }
  },
  beforeDestroy () {
    this['networkConfig/setShowNetworkAlertModal'](false)
    this['networkConfig/setNetworkHasChanged'](false)
  }
}
</script>

<style lang="scss">
main.network--content.container {
  padding-top: 0;
}

.tabs--client-name {
  opacity: 1 !important;

  a {
    line-height: 1rem;
    border: none;
    padding-left: 2rem;
    font-weight: 600;
    font-size: 1.25rem;
    color: #444;
  }
}

.network--tabs {
  .tabs ul {
    padding-top: 5.9rem;
  }

  .tabs li:not(.tabs--client-name) {
    border: 1px solid #00a69e;
    border-bottom: none;
    width: 9.25rem;

    a,
    a:hover {
      font-weight: 700;
      color: #00a69e;
      border-bottom: none;
    }

    &:not(:last-child) {
      margin-right: 0.125rem;
    }

    &.is-active {
      background-color: #00a69e;

      a {
        color: #fff;
      }
    }

    &.is-disabled {
      border: none;
      background-color: #ebebeb;
      cursor: not-allowed;
      opacity: 1 !important;

      a {
        pointer-events: all;
        color: #bbbdbf;
      }
      a:active {
        pointer-events: none;
      }
    }

    .tooltip-content {
      width: 20rem;
      white-space: pre-wrap;
      margin-bottom: 0.5rem;
      font-weight: 600;
      text-align: left;
      border-radius: 2px;
      padding: 0.5rem;
    }
  }

  .networks-tooltip {
    .tooltip-content {
      margin-left: 85px;
    }
  }
}

.config--save-container {
  position: absolute;
  right: 0;
  top: 6rem;
  transform: translateY(-50%);
  display: flex;

  > button {
    background-color: #ec008c;
    color: #fff;
    font-weight: 700;
    padding: 1rem 2.5rem;
    width: 7rem;
    height: 3rem;

    &:disabled,
    &:hover,
    &:active,
    &:focus {
      border-color: #ec008c;
      color: #ec008c;
      background-color: #ec008c;
      color: #fff;
    }
  }

  .submit--alert {
    margin-top: 2.5rem;
  }

  .save--alert {
    margin-right: 2.5rem;

    .error--bold {
      font-weight: 700;
    }
  }
}
</style>
